import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import TooltipBox from "./Tooltip";
import "../css/DocumentManagement.css";
import { fileViewerColumns } from "../js/tableColumns";
import { userData, formatValueUnit, selectedOptic } from "../js/utils";
import { mimeTypes } from "../js/fileManagement";
import { post } from "../js/post";
import FileDisplay from "./FileDisplay";
import ActionPopup from "./ActionPopup";
import { SendFile } from "./SendFile";

export function PatientDocumentManagement({
  patientData,
  createPopupQuestion,
  removePopupQuestion,
  onClose,
  onUpdate,
}) {
  const [fileList, setFileList] = useState([]);
  const [actionComponent, setActionComponent] = useState(null);

  const getFiles = useCallback(async () => {
    const response = await post("ListarFicheros.php", {
      token: userData.token,
      optica: selectedOptic.id,
      idPaciente: patientData.id,
      categoria: [
        "LOPD",
        "LC",
        "ORTOK",
        "AUDIO",
        "CartaGarantia",
        "JustificanteAsistencia",
      ],
    });
    // console.log(
      // "LISTANDO FICHEROS: ",
      // {
        // token: userData.token,
        // optica: selectedOptic.id,
        // idPaciente: patientData.id,
        // categoria: [
          // "LOPD",
          // "LC",
          // "ORTOK",
          // "AUDIO",
          // "CartaGarantia",
          // "JustificanteAsistencia",
        // ],
      // },
      // response,
    // );
    const files = response
      .flatMap((e) => e.ficheros)
      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    setFileList(files);
    onUpdate(files.length);
  }, [patientData.id, onUpdate]);

  const getFile = useCallback(async (id, nombre, open = false) => {
    const response = await post("ObtenerFichero.php", {
      token: userData.token,
      idFichero: id,
    });

    const ext = nombre.split(".").pop().toLowerCase();
    const mime = mimeTypes[ext] || "application/octet-stream";

    if (open) {
      const dataUrl = response.fichero.startsWith("data:")
        ? response.fichero
        : `data:${mime};base64,${response.fichero}`;
      setActionComponent({
        name: "Visor de fichero",
        component: (
          <div className="container-panel file-info">
            <FileDisplay base64Data={dataUrl} />
          </div>
        ),
        fullscreen: true,
      });
    } else {
      const rawBase64 = response.fichero.startsWith("data:")
        ? response.fichero.split(",")[1]
        : response.fichero;
      const byteChars = atob(rawBase64);
      const byteArray = new Uint8Array(
        [...byteChars].map((c) => c.charCodeAt(0)),
      );
      const blob = new Blob([byteArray], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, []);

  const deleteFile = useCallback(async (id) => {
    await post("support-extra/gestionDocumental/EliminarFicheroGD.php", {
      token: userData.token,
      idFichero: id,
    });
  }, []);

  const updatedColumns = useMemo(
    () =>
      fileViewerColumns.map((col) => {
        if (col.name === "") {
          return {
            ...col,
            width: "120px",
            selector: (row) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <button
                  onClick={async () => {
                    await getFile(row.idFichero, row.nombreFichero, true);
                  }}
                >
                  <img
                    src="/images/open-file-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"open" + String(row.idFichero)}
                    alt="ver"
                  ></img>
                  <TooltipBox
                    id={"open" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Abrir
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={async () => {
                    await getFile(row.idFichero, row.nombreFichero);
                  }}
                >
                  <img
                    src="/images/download-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"download" + String(row.idFichero)}
                    alt="descargar"
                  ></img>
                  <TooltipBox
                    id={"download" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Descargar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={() => {
                    setActionComponent({
                      name: "Envío de fichero",
                      component: (
                        <SendFile
                          id={row.idFichero}
                          createPopupQuestion={createPopupQuestion}
                          removePopupQuestion={removePopupQuestion}
                        />
                      ),
                      fullscreen: false,
                    });
                  }}
                >
                  <img
                    src="/images/send-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"send" + String(row.idFichero)}
                    alt="enviar"
                  ></img>
                  <TooltipBox
                    id={"send" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Enviar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={() => {
                    createPopupQuestion(
                      `¿Desea eliminar el fichero ${row.nombreFichero}?`,
                      async () => {
                        await deleteFile(row.idFichero);
                        await getFiles();
                        removePopupQuestion();
                      },
                      removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }}
                >
                  <img
                    src="/images/remove-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"delete" + String(row.idFichero)}
                    alt="eliminar"
                  ></img>
                  <TooltipBox
                    id={"delete" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Eliminar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
              </div>
            ),
          };
        }

        return col;
      }),
    [getFile, getFiles, deleteFile, createPopupQuestion, removePopupQuestion],
  );

  useEffect(() => {
    getFiles();
  }, [getFiles]);

  return (
    <div className="popup-container" style={{ zIndex: 9 }}>
      {actionComponent !== null && (
        <ActionPopup
          name={actionComponent.name}
          children={actionComponent.component}
          fullscreen={actionComponent.fullscreen}
          onClose={() => {
            setActionComponent(null);
          }}
        ></ActionPopup>
      )}
      <div className="ap-container popup-screen" style={{ zIndex: 9 }}>
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">Gestión documental</span>
          <button className="popup-action-icon close-icon" onClick={onClose}>
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className="tab-content ap-content"
          style={{ overflow: "unset", backgroundColor: "#f9f9f9" }}
        >
          <div
            className="container-panel fv-col-sec"
            style={{ minWidth: "800px", gap: 20 }}
          >
            <div className="dashboard-dm-header">
              <div
                className="opt-table-title opt-table-title-button"
                style={{ marginBottom: 0 }}
              >
                <div>
                  Listado de ficheros ({formatValueUnit(fileList.length)})
                </div>
              </div>
            </div>
            <DataTable
              columns={updatedColumns}
              data={fileList}
              className="custom-datatable dashboard-custom-table patient-dm"
              noDataComponent="No hay datos disponibles."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
